import type { BoardDefinition } from '@skyrover/shared/types';

import { getActiveBoard } from '../../board-context.js';

/** How beginner pin labels are formatted for the active board family. */
export type BeginnerPinLabelStyle = 'arduino-d' | 'gpio' | 'raw';

/** Pin roles future beginner components can request from the same selector. */
export type BeginnerPinRole = 'digital' | 'button' | 'led' | 'pwm' | 'analog' | 'buzzer';

export type BeginnerPinOption = [string, string];

function detectPinLabelStyle(board: BoardDefinition | null): BeginnerPinLabelStyle {
  if (!board) {
    return 'arduino-d';
  }
  const id = board.id.toLowerCase();
  const mcu = board.mcu.toLowerCase();
  const platform = board.compiler.platform.toLowerCase();
  if (id.includes('esp') || mcu.includes('esp') || platform.includes('esp')) {
    return 'gpio';
  }
  if (id.includes('arduino') || platform.includes('avr') || platform.includes('megaavr')) {
    return 'arduino-d';
  }
  return 'raw';
}

/** Format a pin number/name for beginner-friendly display (e.g. D2, GPIO0). */
export function formatBeginnerPinLabel(
  pin: string | number,
  board: BoardDefinition | null = getActiveBoard(),
): string {
  const pinText = String(pin);
  const style = detectPinLabelStyle(board);

  if (style === 'arduino-d') {
    if (/^A\d+$/i.test(pinText) || pinText.startsWith('D') || pinText.startsWith('GPIO')) {
      return pinText;
    }
    return `D${pinText}`;
  }

  if (style === 'gpio') {
    if (pinText.startsWith('GPIO') || /^A\d+$/i.test(pinText)) {
      return pinText;
    }
    return `GPIO${pinText}`;
  }

  return pinText;
}

function uniquePinOptions(options: BeginnerPinOption[]): BeginnerPinOption[] {
  const seen = new Set<string>();
  const unique: BeginnerPinOption[] = [];
  for (const option of options) {
    if (seen.has(option[1])) {
      continue;
    }
    seen.add(option[1]);
    unique.push(option);
  }
  return unique;
}

function digitalPinsForBoard(board: BoardDefinition | null): Array<string | number> {
  if (!board) {
    return [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  }
  return board.pins.digital;
}

function pwmPinsForBoard(board: BoardDefinition | null): Array<string | number> {
  if (!board) {
    return [3, 5, 6, 9, 10, 11];
  }
  return board.pins.pwm ?? board.pins.digital;
}

function analogPinsForBoard(board: BoardDefinition | null): string[] {
  if (!board) {
    return ['A0', 'A1', 'A2', 'A3', 'A4', 'A5'];
  }
  const analog = board.pins.analog ?? [];
  if (analog.length > 0) {
    return analog;
  }
  return board.pins.digital.slice(0, 6).map(String);
}

/**
 * Board-aware beginner pin options.
 * Labels are friendly (D2 / GPIO0); values stay raw pin ids for future codegen.
 */
export function getBeginnerPinOptions(role: BeginnerPinRole = 'digital'): BeginnerPinOption[] {
  const board = getActiveBoard();
  let pins: Array<string | number>;

  switch (role) {
    case 'pwm':
      pins = pwmPinsForBoard(board);
      if (pins.length === 0) {
        pins = digitalPinsForBoard(board);
      }
      break;
    case 'analog':
      pins = analogPinsForBoard(board);
      break;
    case 'led':
    case 'button':
    case 'buzzer':
    case 'digital':
    default:
      pins = digitalPinsForBoard(board);
      break;
  }

  const options = pins.map((pin): BeginnerPinOption => [
    formatBeginnerPinLabel(pin, board),
    String(pin),
  ]);

  return uniquePinOptions(options);
}

/** JSON dropdown field bound to the reusable beginner pin selector. */
export function beginnerPinField(
  name: string,
  role: BeginnerPinRole = 'digital',
): {
  type: 'field_dropdown';
  name: string;
  options: () => BeginnerPinOption[];
} {
  return {
    type: 'field_dropdown',
    name,
    options: () => getBeginnerPinOptions(role),
  };
}

/** Convenience aliases for common beginner components. */
export function getBeginnerButtonPinOptions(): BeginnerPinOption[] {
  return getBeginnerPinOptions('button');
}

export function getBeginnerDigitalPinOptions(): BeginnerPinOption[] {
  return getBeginnerPinOptions('digital');
}
